import React, { useState } from "react"
import {
  Modal,
  Button,
  Select,
  InputNumber,
  Space,
  Typography,
  Spin,
  Result,
  Alert,
  Table,
  Tooltip,
} from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHatWizard, faWandMagicSparkles } from "@fortawesome/pro-regular-svg-icons"
import { gen, swimminglyApi } from "./utils/helperFunctions"

interface EventSummaryRow {
  swimEventId: number
  sortOrder: number
  label: string
  count: number
}

interface MagicResultsModalProps {
  meetId: number
  meetName: string
  resultSource?: 'fileImport' | 'scorekeeper' | 'testGenerated' | null
  meetstatus?: 0 | 1 | 2 | null
  seasonEnded?: boolean
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

type ModalStep = "prompt" | "options" | "loading" | "success" | "blocked" | "error"

export default function MagicResultsModal({
  meetId,
  meetName,
  resultSource,
  meetstatus,
  seasonEnded = false,
  open,
  onClose,
  onSuccess,
}: MagicResultsModalProps): JSX.Element {
  const createResultsDisabled =
    seasonEnded ||
    resultSource === 'scorekeeper' ||
    resultSource === 'fileImport' ||
    (resultSource == null && meetstatus === 2)
  const [step, setStep] = useState<ModalStep>("prompt")
  const [swimmerSource, setSwimmerSource] = useState<"entries" | "clubs">("entries")
  const [maxPerEvent, setMaxPerEvent] = useState<number>(8)
  const [summary, setSummary] = useState<EventSummaryRow[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [blockedMessage, setBlockedMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const reset = () => {
    setStep("prompt")
    setSwimmerSource("entries")
    setMaxPerEvent(8)
    setSummary([])
    setTotalResults(0)
    setBlockedMessage("")
    setErrorMessage("")
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleGenerate = async () => {
    setStep("loading")
    try {
      const data: any = await swimminglyApi
        .post(gen("/api/generateMagicResults"))
        .body({ meetId, swimmerSource, maxPerEvent })

      if (data.status === "blocked") {
        setBlockedMessage(data.message || "Blocked: meet has real results.")
        setStep("blocked")
        return
      }
      if (data.status === "success") {
        setSummary(data.summary || [])
        setTotalResults(data.totalResults || 0)
        setStep("success")
        onSuccess()
        return
      }
      setErrorMessage(data.message || "An unexpected error occurred.")
      setStep("error")
    } catch (err: any) {
      setErrorMessage(String(err?.message || "Network error"))
      setStep("error")
    }
  }

  const footerForStep: Record<ModalStep, React.ReactNode> = {
    prompt: (
      <Space>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          disabled={createResultsDisabled}
          icon={<FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6 }} />}
          onClick={() => setStep("options")}
        >
          Let's do it
        </Button>
      </Space>
    ),
    options: (
      <Space>
        <Button onClick={() => setStep("prompt")}>Back</Button>
        <Button type="primary" onClick={handleGenerate}>
          Generate Results
        </Button>
      </Space>
    ),
    loading: null,
    success: (
      <Button type="primary" onClick={handleClose}>
        Done
      </Button>
    ),
    blocked: (
      <Button onClick={handleClose}>Close</Button>
    ),
    error: (
      <Space>
        <Button onClick={() => setStep("options")}>Try Again</Button>
        <Button onClick={handleClose}>Close</Button>
      </Space>
    ),
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={footerForStep[step]}
      width={560}
      title={
        <Space size={10}>
          <FontAwesomeIcon icon={faHatWizard} style={{ fontSize: 18, color: "#722ed1" }} />
          <span>Magic Results</span>
        </Space>
      }
    >
      {step === "prompt" && (
        <div style={{ padding: "8px 0" }}>
          <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
            What kind of magic would you like to sprinkle on this swim meet?
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 20 }}>
            Meet: <strong>{meetName}</strong>
          </Typography.Paragraph>
          <Tooltip
            title={createResultsDisabled ? (seasonEnded ? "Test results cannot be generated for past seasons." : "This meet already has real results. Test results can only be generated on meets that have no results or previously generated test results.") : ""}
          >
          <div
            style={{
              border: `2px solid ${createResultsDisabled ? "#d9d9d9" : "#722ed1"}`,
              borderRadius: 8,
              padding: "14px 16px",
              background: createResultsDisabled ? "#fafafa" : "#f9f0ff",
              cursor: createResultsDisabled ? "not-allowed" : "pointer",
              opacity: createResultsDisabled ? 0.55 : 1,
            }}
            onClick={createResultsDisabled ? undefined : () => setStep("options")}
          >
            <Space size={12} align="start">
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                style={{ fontSize: 22, color: "#722ed1", marginTop: 2 }}
              />
              <div>
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Create results
                </Typography.Text>
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 12, marginBottom: 0, marginTop: 4 }}
                >
                  A real set of results based on the clubs and swimmers in the meet will be
                  created for this event order. Times will appear as realistic for the given
                  event and age group.
                </Typography.Paragraph>
              </div>
            </Space>
          </div>
          </Tooltip>
        </div>
      )}

      {step === "options" && (
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            title="This will permanently overwrite any existing test results for this meet and publish it."
          />
          <div className="edit-field-stack">
            <span className="edit-field-label">Swimmer source</span>
            <Select
              value={swimmerSource}
              onChange={setSwimmerSource}
              style={{ width: "100%" }}
              options={[
                {
                  value: "entries",
                  label: "Swimmers entered in this meet (meet_entries)",
                },
                {
                  value: "clubs",
                  label: "All active swimmers on clubs in this meet",
                },
              ]}
            />
          </div>
          <div className="edit-field-stack">
            <span className="edit-field-label">Max swimmers per event</span>
            <InputNumber
              min={1}
              max={50}
              value={maxPerEvent}
              onChange={(v) => setMaxPerEvent(v ?? 8)}
              style={{ width: 120 }}
            />
          </div>
        </Space>
      )}

      {step === "loading" && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin
            indicator={
              <FontAwesomeIcon
                icon={faHatWizard}
                spin
                style={{ fontSize: 42, color: "#722ed1" }}
              />
            }
          />
          <Typography.Title level={4} style={{ marginTop: 20, color: "#722ed1" }}>
            Creating the magic…
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Generating realistic results for every event in the order.
          </Typography.Text>
        </div>
      )}

      {step === "success" && (
        <div>
          <Result
            status="success"
            title={`${totalResults} result${totalResults !== 1 ? "s" : ""} created`}
            subTitle="Meet has been published with test results."
            style={{ paddingTop: 8, paddingBottom: 8 }}
          />
          <Table<EventSummaryRow>
            size="small"
            pagination={false}
            scroll={{ y: 280 }}
            dataSource={summary.map((r) => ({ ...r, key: r.swimEventId }))}
            columns={[
              {
                title: "Event",
                dataIndex: "label",
                ellipsis: true,
              },
              {
                title: "Results",
                dataIndex: "count",
                width: 80,
                align: "right",
              },
            ]}
          />
        </div>
      )}

      {step === "blocked" && (
        <Alert
          type="error"
          showIcon
          title="Cannot generate test results"
          description={blockedMessage}
          style={{ marginTop: 8 }}
        />
      )}

      {step === "error" && (
        <Alert
          type="error"
          showIcon
          title="Something went wrong"
          description={errorMessage}
          style={{ marginTop: 8 }}
        />
      )}
    </Modal>
  )
}
